import { useState } from 'react'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { EarnAssetsList } from '../../components/EarnAssetsList'
import { EarnNoPortfolio } from '../../components/EarnNoPortfolio'
import { EarnNoProducts } from '../../components/EarnNoProducts'
import { EarnNoWallets } from '../../components/EarnNoWallets'
import { EarnPageHeader } from '../../components/EarnPageHeader'
import { EarnEmbeddedCustomerRewardsProgram } from '../../components/embedded/EarnEmbeddedCustomerRewardsProgram'
import { EarnEmbeddedDistributionDialog } from '../../components/embedded/EarnEmbeddedDistributionDialog'
import { EarnEmbeddedMyRewards } from '../../components/embedded/EarnEmbeddedMyRewards'
import { useEarnEmbeddedDashboardData } from '../../hooks/dashboard/useEarnEmbeddedDashboardData'
import { EARN_DASHBOARD_STATE } from '../../types'
import { UseEarnPageDataHandlers } from '../../types/dashboardState'

export type EarnEmbeddedViewProps = UseEarnPageDataHandlers

export const EarnEmbeddedView = ({ onDeposit, onWithdraw }: EarnEmbeddedViewProps) => {
  const viewData = useEarnEmbeddedDashboardData({ onDeposit, onWithdraw })
  const [isDistributionDialogOpen, setIsDistributionDialogOpen] = useState(false)

  if (viewData.dashboardState === EARN_DASHBOARD_STATE.LOADING) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (viewData.dashboardState === EARN_DASHBOARD_STATE.NO_PRODUCTS) {
    return <EarnNoProducts />
  }

  if (viewData.dashboardState === EARN_DASHBOARD_STATE.NO_WALLETS) {
    return <EarnNoWallets featuredProduct={viewData.featuredProduct} />
  }

  if (viewData.dashboardState === EARN_DASHBOARD_STATE.NO_PORTFOLIOS) {
    return <EarnNoPortfolio assets={viewData.assets} showCustomerEarnRate />
  }

  const {
    assets,
    epcTakesEarn,
    distributionProductId,
    summary,
    totalEarnings,
    customerRewardsProgram,
    canManageDistribution,
    isStatisticsLoading,
  } = viewData

  return (
    <>
      <EarnPageHeader />
      <Box flexCol gap={24}>
        <Box flexCol gap={24} className="items-stretch lg:flex-row">
          <Box className="flex-1 min-w-0">
            <EarnEmbeddedMyRewards
              allocatedFunds={summary.allocatedFunds}
              availableFunds={summary.availableFunds}
              isStatisticsLoading={isStatisticsLoading}
              totalEarnings={totalEarnings.totalEarnings}
              allocationEarnings={totalEarnings.allocationEarnings}
              customerEarnings={totalEarnings.customerEarnings}
            />
          </Box>
          <Box className="flex-1 min-w-0">
            <EarnEmbeddedCustomerRewardsProgram
              onDistributeEarningsClick={() => setIsDistributionDialogOpen(true)}
              epcTakesEarn={epcTakesEarn}
              canManageDistribution={canManageDistribution}
              isStatisticsLoading={isStatisticsLoading}
              {...customerRewardsProgram}
            />
          </Box>
        </Box>
        <EarnAssetsList assets={assets} showCustomerEarnRate />
        {distributionProductId && (
          <EarnEmbeddedDistributionDialog
            open={isDistributionDialogOpen}
            onClose={() => setIsDistributionDialogOpen(false)}
            productId={distributionProductId}
          />
        )}
      </Box>
    </>
  )
}
